import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Sets up a real-time listener for a user's conversations
 * @param {string} uid - User ID
 * @param {Function} onUpdate - Callback function that receives array of conversations
 * @returns {Function} Unsubscribe function
 */
export const listenConversations = (uid, onUpdate) => {
  // Create reference to the conversations collection
  const conversationsRef = collection(db, `messagingplatform/${uid}/conversations`);
  
  // Create query ordered by lastTimestamp descending
  const conversationsQuery = query(
    conversationsRef,
    orderBy('lastTimestamp', 'desc')
  );
  
  // Set up the listener
  const unsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
    const conversations = snapshot.docs.map(doc => {
      const data = doc.data();
      // Parse the conversation ID to get aliasNumber and lastContactNumber
      const [aliasNumber, lastContactNumber] = doc.id.split('_');
      
      // Fix timestamp handling - use conditional to handle different types
      let lastTimestampDate = null;
      if (data.lastTimestamp) {
        if (data.lastTimestamp.toDate) {
          // It's a Firestore timestamp
          lastTimestampDate = data.lastTimestamp.toDate();
        } else if (data.lastTimestamp instanceof Date) {
          // It's already a Date object
          lastTimestampDate = data.lastTimestamp;
        } else if (typeof data.lastTimestamp === 'string') {
          // It's an ISO string
          try {
            lastTimestampDate = new Date(data.lastTimestamp);
            // Validate date
            if (isNaN(lastTimestampDate.getTime())) {
              lastTimestampDate = null;
            }
          } catch (e) {
            console.error('Invalid timestamp string:', data.lastTimestamp);
            lastTimestampDate = null;
          }
        }
      }
      
      // Filter out spam warning messages from lastMessage display for senders
      let displayLastMessage = data.lastMessage || '';
      if (data.lastMessage && data.lastMessage.includes('SAFETY NOTICE: The sender of the previous message is using ChatOdyssey')) {
        displayLastMessage = ''; // Hide warning messages from sender's conversation list
      }

      return {
        id: doc.id,
        platformNumber: data.platformNumber || aliasNumber,
        aliasNumber,
        lastContactNumber,
        otherParty: data.otherParty || lastContactNumber,
        lastMessage: displayLastMessage,
        lastTimestamp: lastTimestampDate,
        unreadCount: data.unreadCount || 0,
        connectionStatus: 'active',
        messageCollections: []
      };
    });
    
    // Call the callback with the mapped data
    onUpdate(conversations);
  }, (error) => {
    console.error('Error listening to conversations:', error);
  });
  
  // Return unsubscribe function to be called when component unmounts
  return unsubscribe;
};

/**
 * Sets up a real-time listener for messages in a specific conversation
 * @param {string} uid - User ID
 * @param {string} platformNumber - Platform phone number
 * @param {string} otherNumber - Other party's phone number
 * @param {Function} onUpdate - Callback function that receives array of messages
 * @returns {Function} Unsubscribe function
 */
export const listenMessages = (uid, platformNumber, otherNumber, onUpdate) => {
  // Build the conversation ID
  const convoId = `${platformNumber}_${otherNumber}`;
  
  // Create reference to the messages collection
  const messagesRef = collection(
    db, 
    `messagingplatform/${uid}/conversations/${convoId}/messages`
  );
  
  // Create query ordered by timestamp ascending
  const messagesQuery = query(
    messagesRef,
    orderBy('timestamp', 'asc')
  );
  
  // Set up the listener
  const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
    const messages = snapshot.docs.map(doc => {
      const data = doc.data();
      
      // Properly handle the timestamp
      let messageTimestamp = null;
      if (data.timestamp) {
        if (data.timestamp.toDate) {
          // It's a Firestore timestamp
          messageTimestamp = data.timestamp.toDate();
        } else if (data.timestamp instanceof Date) {
          // It's already a Date object
          messageTimestamp = data.timestamp;
        } else if (typeof data.timestamp === 'string') {
          // It's an ISO string
          try {
            messageTimestamp = new Date(data.timestamp);
            // Validate date
            if (isNaN(messageTimestamp.getTime())) {
              messageTimestamp = null;
            }
          } catch (e) {
            console.error('Invalid timestamp string:', data.timestamp);
            messageTimestamp = null;
          }
        }
      }
      
      return {
        id: doc.id,
        direction: data.direction,
        body: data.body,
        text: data.body, // Add text for compatibility with UI
        timestamp: data.timestamp, // Keep original for Firestore format
        timestampDate: messageTimestamp, // Add a JavaScript Date version
        status: data.status,
        metadata: data.metadata,
        mediaUrls: data.mediaUrls, // Add mediaUrls for MMS support
        // Add Telnyx status fields for outbound messages
        sendToTelnyx: data.sendToTelnyx,
        sentAt: data.sentAt,
        telnyxResponse: data.telnyxResponse,
        telnyxMessageId: data.telnyxMessageId,  // Telnyx message ID for webhook lookups
        errorMessage: data.errorMessage,
        // Add webhook status fields for real-time updates
        completedAt: data.completedAt,
        errorCode: data.errorCode,
        lastStatusUpdate: data.lastStatusUpdate,
        telnyxStatusPayload: data.telnyxStatusPayload,
        // Add properties for UI compatibility
        from: {
          number: data.direction === 'outbound' ? platformNumber : otherNumber
        },
        to: {
          number: data.direction === 'outbound' ? otherNumber : platformNumber,
          status: data.status
        },
        // Add received_at for compatibility with existing code
        received_at: messageTimestamp ? messageTimestamp.toISOString() : null
      };
    })
    .filter(message => {
      // Filter out spam warning messages from the sender's view
      // These messages should only be visible to recipients, not to the sender
      if (message.metadata?.isSpamWarning && message.metadata?.hideFromSender) {
        return false;
      }
      return true;
    });
    
    // Call the callback with the mapped data
    onUpdate(messages);
  }, (error) => {
    console.error('Error listening to messages:', error);
  });
  
  // Return unsubscribe function to be called when component unmounts
  return unsubscribe;
}; 